import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { FieldArray, type FormikProps } from 'formik';
import { forwardRef, useImperativeHandle, useMemo } from 'react';

import './SpecificationIntFloatPopupContent.scss';

import { SelectField } from 'src/components/base/fields/SelectField';
import type { SpecificationIntFloatPopupFormValues } from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatPopup.validation';
import { TextInputField } from 'src/components/base/fields/TextInputField';
import type { ISelectOption } from 'src/types';
import {
  SPECIFICATION_MEASURE_UNIT_MAPPER,
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

function MeasureUnitOptionRender({ value }: { value?: string | number }) {
  return `${SPECIFICATION_MEASURE_UNIT_MAPPER[value as SpecificationMeasureUnit]} (${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value as SpecificationMeasureUnit]})`;
}

export type SpecificationIntFloatPopupFormContentRef = {
  submit?: () => Promise<void>;
  isDisabled?: boolean;
};

type SpecificationIntFloatPopupFormContentProps = {
  formikProps: FormikProps<SpecificationIntFloatPopupFormValues>;

  multiselect: boolean;
  defaultMeasureUnit: SpecificationMeasureUnit | null;
  allowedMeasureUnits: SpecificationMeasureUnit[];
};

export const SpecificationIntFloatPopupFormContent = forwardRef<
  SpecificationIntFloatPopupFormContentRef,
  SpecificationIntFloatPopupFormContentProps
>(({ formikProps: { values, isValid, submitForm }, multiselect, defaultMeasureUnit, allowedMeasureUnits }, ref) => {
  useImperativeHandle<SpecificationIntFloatPopupFormContentRef, SpecificationIntFloatPopupFormContentRef>(
    ref,
    () => ({
      submit: submitForm,
      isDisabled: !isValid,
    }),
    [submitForm, isValid]
  );

  const measureUnitsOptions: ISelectOption[] = useMemo(
    () =>
      allowedMeasureUnits.map((allowedMeasureUnit) => ({
        label: SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[allowedMeasureUnit],
        value: allowedMeasureUnit,
      })),
    [allowedMeasureUnits]
  );

  return (
    <div className="specification-int-float-popup-content__container">
      <FieldArray
        name="values"
        render={(arrayHelpers) => {
          const hasMeasureUnits = multiselect && measureUnitsOptions.length > 0;

          const gridLayoutItems = [
            '1fr', // Value

            ...(hasMeasureUnits ? ['70px'] : []), // Measure units
            ...(multiselect ? ['max-content'] : []), // Remove item button
          ].join(' ');

          return (
            <>
              {values.values.map((_, index) => (
                <div
                  key={String(index)}
                  className="specification-int-float-popup-content__item"
                  style={{
                    gridTemplateColumns: gridLayoutItems,
                  }}
                >
                  <TextInputField
                    name={`values[${index}].value`}
                    type="number"
                    className="w-100"
                    label={`Value ${index + 1}`}
                    touchOnMount={true}
                    addonAfter={
                      !multiselect &&
                      defaultMeasureUnit &&
                      defaultMeasureUnit !== SpecificationMeasureUnit.NONE && (
                        <span>{SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[defaultMeasureUnit]}</span>
                      )
                    }
                  />
                  {hasMeasureUnits && (
                    <SelectField
                      className="specification-int-float-popup-content__measure-units"
                      name={`values[${index}].measureUnit`}
                      options={measureUnitsOptions}
                      popupMatchSelectWidth={false}
                      touchOnMount={true}
                      optionRender={({ value }) => <MeasureUnitOptionRender value={value} />}
                      getPopupContainer={() =>
                        // Render popup in current element to get click events on dropdown inside `specification-int-float-popup` element
                        (document.getElementsByClassName('specification-int-float-popup')?.[0] as HTMLElement) ??
                        document.body
                      }
                    />
                  )}
                  {multiselect && (
                    <Button
                      className="specification-int-float-popup-content__button"
                      title="Remove value"
                      type="primary"
                      danger={true}
                      size="middle"
                      icon={<MinusOutlined />}
                      disabled={values.values.length < 2}
                      onClick={arrayHelpers.handleRemove(index)}
                    />
                  )}
                </div>
              ))}
              {multiselect && (
                <Button
                  className="specification-int-float-popup-content__button specification-int-float-popup-content__add-button"
                  title="Add value"
                  type="primary"
                  size="middle"
                  icon={<PlusOutlined />}
                  onClick={arrayHelpers.handlePush({
                    value: null,
                    measureUnit: defaultMeasureUnit,
                  } as SpecificationIntFloatPopupFormValues['values'][0])}
                />
              )}
            </>
          );
        }}
      />
    </div>
  );
});
